import type { Form } from 'factorial-form'
import { RegularSelectOptionsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { useCachedData } from 'modules/automations/components/Show/Providers/CachedDataProvider'
import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import { ITriggerCondition } from 'modules/automations/store/models/Trigger'
import { bankAccountFormatOptions } from 'modules/banking/lib/bankAccountNumbersHelpers'

import {
  AllContractTypes,
  AllProfessionalCategories,
  AllWorkingDayTypes,
  CotizationGroupOptions,
  DocumentFolderOptions,
  EmployeeEducationLevelOptions,
  JobCatalogRoles,
  LeaveTypeDefaultOptions,
  LeaveTypeOptions,
  LocationOptions,
  SalaryFrequencyOptions,
  SpainProfessionalCategories,
  TeamOptions,
  booleanOptions,
  countryOptions,
  documentTypeOptions,
  employeeActiveOptions,
  genderOptions,
  reviewStatusOptions,
  workingHoursFrequencyOptions,
} from './options'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  condition?: ITriggerCondition
}

const RegularSelectClass = ({ disabled, form, operation, condition }: Props) => {
  const includeContractTypes = operation.attribute === TriggerAttributes.contractType
  const includeFrenchWorkTypes = operation.attribute === TriggerAttributes.workingDayType
  const includeFrenchProfessionalCategories =
    operation.attribute === TriggerAttributes.professionalCategory
  const includeSpanishWorkingDayTypes = operation.attribute === TriggerAttributes.workingDayType
  const includeSpanishProfessionalCategories =
    operation.attribute === TriggerAttributes.professionalCategory ||
    operation.attribute === TriggerAttributes.spainProfessionalCategories
  const includeSpanishEducationLevels = operation.attribute === TriggerAttributes.educationLevel
  const { data } = useQuery(RegularSelectOptionsDocument, {
    variables: {
      includeContractTypes,
      includeFrenchWorkTypes,
      includeFrenchProfessionalCategories,
      includeSpanishWorkingDayTypes,
      includeSpanishProfessionalCategories,
      includeSpanishEducationLevels,
    },
  })

  const {
    data: { legalEntities },
  } = useCachedData()

  const costCenters = data?.finance.costCentersConnection.nodes.map((costCenter) => ({
    value: costCenter.id,
    label: costCenter.name,
  }))

  const employeeGroups = data?.employees.employeeGroupsConnection.nodes.map((employeeGroup) => ({
    value: employeeGroup.id,
    label: employeeGroup.name,
  }))

  const mappedLegalEntities =
    legalEntities?.map((legalEntity) => ({
      value: legalEntity.id,
      label: legalEntity.legalName,
    })) || []

  const options = () => {
    const attr = operation.attribute

    if (condition?.options) return condition.options

    switch (attr) {
      case TriggerAttributes.review_request_state:
        return reviewStatusOptions()

      case TriggerAttributes.folder_id:
        return DocumentFolderOptions()

      case TriggerAttributes.document_type:
        return documentTypeOptions()

      case TriggerAttributes.leave_type_id:
        return LeaveTypeOptions()

      case TriggerAttributes.gender:
        return genderOptions()

      case TriggerAttributes.location_id:
        return LocationOptions()

      case TriggerAttributes.team_id:
        return TeamOptions()

      case TriggerAttributes.country:
      case TriggerAttributes.nationality:
        return countryOptions()

      case TriggerAttributes.spainProfessionalCategories:
        return SpainProfessionalCategories(data?.contracts)

      case TriggerAttributes.professionalCategory:
        return AllProfessionalCategories(data?.contracts)

      case TriggerAttributes.workingHoursFrequency:
        return workingHoursFrequencyOptions()
      case TriggerAttributes.bankNumberFormat:
        return bankAccountFormatOptions()
      case TriggerAttributes.employeeActive:
        return employeeActiveOptions()

      case TriggerAttributes.leave_type:
        return LeaveTypeDefaultOptions()

      case TriggerAttributes.educationLevel:
        return EmployeeEducationLevelOptions(data?.contracts)

      case TriggerAttributes.cotizationGroup:
        return CotizationGroupOptions()

      case TriggerAttributes.salaryFrequency:
        return SalaryFrequencyOptions()

      case TriggerAttributes.workingDayType:
        return AllWorkingDayTypes(data?.contracts)

      case TriggerAttributes.contractType:
        return AllContractTypes(data?.contracts)

      case TriggerAttributes.jobCatalogRole:
        return JobCatalogRoles()

      case TriggerAttributes.costCenterId:
        return costCenters || []

      case TriggerAttributes.employeeGroupId:
        return employeeGroups || []

      case TriggerAttributes.legalEntityId:
        return mappedLegalEntities || []

      default:
        return booleanOptions(attr)
    }
  }

  return (
    <Select
      none={i18n.t('common.none')}
      readonly={disabled}
      field={form.get('value')}
      label={i18n.t('automations.actions.branch.value')}
      options={options()}
    />
  )
}

export const RegularSelect = observer(RegularSelectClass)
